import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { AirplaneInFlight, ArrowClockwise, Clock, SealCheck, Ticket, Wallet, Warning } from "@phosphor-icons/react";
import moment from "moment";
import { HashLoader } from "react-spinners";
import BookedFlightsList from "../../components/BookedFlightsList";
import useFlightBookings from "../../hooks/useFlightBookings";
import useInternationalFlightBookings from "../../hooks/useInternationalFlightBookings";
import { formatAsMoney } from "../../helpers/numbers";
import AviationAmbientBackground from "../../components/AviationAmbientBackground";

import { BookedFlight, TravelInformation } from "../../@types";
import { IntlBookingSummary } from "../../@types/intlFlightAggregator";
import { Link, useLocation } from "react-router-dom";

const PendingLegRow = ({ leg, label }: { leg: TravelInformation; label?: string }) => (
  <div className="flex flex-col gap-2">
    {label && (
      <div className="flex items-center gap-2">
        <span className="text-[10px] font-bold text-amber-500 uppercase tracking-widest shrink-0">
          {label}
        </span>
        <div className="h-[1px] flex-1 bg-amber-100" />
        <span className="text-[10px] font-medium text-gray-400 shrink-0">
          {moment(leg.DepartureDate).format("ddd, DD MMM YYYY")}
        </span>
      </div>
    )}
    <div className="flex items-center gap-3">
      <div className="flex flex-col items-start min-w-[52px]">
        <span className="text-2xl font-bold font-aeonic text-deepBlue">
          {leg.DepartureTerminalCode}
        </span>
        <span className="text-xs font-bold text-gray-400">
          {moment(leg.DepartureDate).format("HH:mm")}
        </span>
      </div>
      <div className="flex-1 flex flex-col items-center gap-0.5 min-w-0">
        <div className="flex items-center w-full gap-1.5">
          <div className="h-[1px] flex-1 bg-amber-100 relative">
            <div className="absolute left-0 top-1/2 -translate-y-1/2 size-1.5 rounded-full bg-amber-200" />
          </div>
          <AirplaneInFlight className="size-4 text-amber-300 shrink-0" weight="fill" />
          <div className="h-[1px] flex-1 bg-amber-100 relative">
            <div className="absolute right-0 top-1/2 -translate-y-1/2 size-1.5 rounded-full bg-amber-200" />
          </div>
        </div>
        <span className="text-[10px] text-gray-400 font-medium truncate max-w-full text-center">
          {leg.OperatorName}
        </span>
      </div>
      <div className="flex flex-col items-end min-w-[52px]">
        <span className="text-2xl font-bold font-aeonic text-deepBlue text-right">
          {leg.ArrivalTerminalCode}
        </span>
        <span className="text-xs font-bold text-gray-400">
          {moment(leg.ArrivalDate).format("HH:mm")}
        </span>
      </div>
    </div>
    <div className="flex justify-between text-[11px] text-gray-400 leading-tight">
      <span className="truncate max-w-[44%]">{leg.DepartureTerminal}</span>
      <span className="truncate max-w-[44%] text-right">{leg.ArrivalTerminal}</span>
    </div>
  </div>
);

type TabKey = "confirmed" | "pending" | "international";

const IntlBookingCard = ({ booking }: { booking: IntlBookingSummary }) => (
  <Link
    to={`/aviation/international/v2/bookings/${booking.id}`}
    className="group relative flex flex-col md:flex-row items-stretch bg-white rounded-2xl border border-gray-100 shadow-[0_4px_20px_-4px_rgba(15,23,42,0.10)] hover:shadow-[0_16px_36px_-8px_rgba(234,92,41,0.15)] hover:border-visaro-orange/30 hover:-translate-y-0.5 transition-all duration-300 overflow-hidden cursor-pointer"
  >
    <div className="absolute top-0 left-0 w-full h-1 md:w-1 md:h-full bg-visaro-orange" />

    <div className="flex-1 p-6 flex flex-col gap-4">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="px-3 py-1 bg-visaro-visaMintGreen text-visaro-visaGreen text-[10px] font-bold rounded-full uppercase tracking-wider flex items-center gap-1">
            <SealCheck weight="fill" className="size-3" />
            {booking.status}
          </span>
          <span className="px-3 py-1 bg-gray-50 text-gray-400 text-[10px] font-medium rounded-full">
            International
          </span>
          {booking.payment_status === "paid" && (
            <span className="px-3 py-1 bg-deepBlue/5 text-deepBlue text-[10px] font-medium rounded-full flex items-center gap-1">
              <Wallet size={11} weight="fill" />
              Paid via Wallet
            </span>
          )}
        </div>
        {booking.booking_reference && (
          <span className="text-sm font-bold font-aeonic text-deepBlue tracking-widest">
            {booking.booking_reference}
          </span>
        )}
      </div>

      <div className="flex items-center gap-4">
        <span className="hidden sm:flex items-center justify-center size-11 rounded-full bg-deepBlue/5 text-deepBlue shrink-0 transition-colors duration-300 group-hover:bg-visaro-orange group-hover:text-white">
          <AirplaneInFlight size={20} weight="duotone" />
        </span>

        <div className="flex-1 flex items-center gap-3 min-w-0">
          <div className="flex flex-col items-start min-w-[52px]">
            <span className="text-2xl font-bold font-aeonic text-deepBlue">{booking.origin ?? "—"}</span>
            {booking.departure_at && (
              <span className="text-xs font-bold text-gray-400">{moment(booking.departure_at).format("HH:mm")}</span>
            )}
          </div>
          <div className="flex-1 flex flex-col items-center gap-0.5 min-w-0">
            <div className="flex items-center w-full gap-1.5">
              <div className="h-[1px] flex-1 bg-gray-200 relative">
                <div className="absolute left-0 top-1/2 -translate-y-1/2 size-1.5 rounded-full bg-gray-300" />
              </div>
              <AirplaneInFlight className="size-4 text-visaro-orange shrink-0" weight="fill" />
              <div className="h-[1px] flex-1 bg-gray-200 relative">
                <div className="absolute right-0 top-1/2 -translate-y-1/2 size-1.5 rounded-full bg-gray-300" />
              </div>
            </div>
            {booking.airline && <span className="text-[10px] text-gray-400 font-medium truncate">{booking.airline}</span>}
          </div>
          <div className="flex flex-col items-end min-w-[52px]">
            <span className="text-2xl font-bold font-aeonic text-deepBlue text-right">{booking.destination ?? "—"}</span>
            {booking.departure_at && (
              <span className="text-xs font-bold text-gray-400">{moment(booking.departure_at).format("D MMM YYYY")}</span>
            )}
          </div>
        </div>
      </div>

      <div className="pt-3 border-t border-gray-50 flex items-center justify-between">
        <div className="flex items-center gap-1.5 text-xs text-gray-400">
          <Ticket size={13} weight="duotone" />
          {booking.created_at ? `Booked ${moment(booking.created_at).format("D MMM YYYY")}` : "Booking"}
        </div>
        <div className="text-right">
          <span className="text-sm font-bold text-deepBlue">
            {booking.currency} {booking.price !== null ? formatAsMoney(booking.price) : "—"}
          </span>
          {booking.price_ngn !== null && (
            <span className="text-xs text-gray-400 ml-2">≈ ₦{formatAsMoney(booking.price_ngn)}</span>
          )}
        </div>
      </div>
    </div>
  </Link>
);

const PendingFlightCard = ({ flight }: { flight: BookedFlight }) => {
  const travelInfos = flight.copy?.TravelInformations ?? [];
  const outbound = travelInfos[0];
  const returnLeg = travelInfos.length > 1 ? travelInfos[travelInfos.length - 1] : null;
  const isReturn = travelInfos.length > 1;
  const passengerCount =
    flight.copy?.PassengerDetails?.AirTravellers?.length ??
    flight.air_travelers?.length ??
    0;

  if (!outbound) return null;

  return (
    <div className="group relative flex flex-col md:flex-row items-stretch bg-white rounded-2xl border border-amber-100 shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden">
      {/* Accent Line */}
      <div className="absolute top-0 left-0 w-full h-1 md:w-1 md:h-full bg-amber-400" />

      <div className="flex-1 p-6 flex flex-col gap-4">
        {/* Header */}
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="px-3 py-1 bg-amber-50 text-amber-700 text-[10px] font-bold rounded-full uppercase tracking-wider flex items-center gap-1">
              <Clock weight="fill" className="size-3" />
              Pending
            </span>
            <span className="px-3 py-1 bg-gray-50 text-gray-500 text-[10px] font-bold rounded-full uppercase tracking-wider">
              {isReturn ? "Return" : "One Way"}
            </span>
            <span className="px-3 py-1 bg-gray-100 text-gray-600 text-[10px] font-bold rounded-full uppercase tracking-wider">
              {outbound.TicketClass || "Economy"}
            </span>
            <span className="px-3 py-1 bg-gray-50 text-gray-400 text-[10px] font-medium rounded-full capitalize">
              {flight.type}
            </span>
          </div>
        </div>

        {/* Flight Legs */}
        <div className="flex flex-col gap-3 opacity-85">
          <PendingLegRow
            leg={outbound}
            label={isReturn ? "Outbound" : undefined}
          />
          {!isReturn && (
            <p className="text-xs text-gray-400 font-medium">
              {moment(outbound.DepartureDate).format("ddd, MMM DD, YYYY")}
            </p>
          )}
          {returnLeg && (
            <>
              <div className="border-t border-dashed border-amber-100" />
              <PendingLegRow leg={returnLeg} label="Return" />
            </>
          )}
        </div>

        {/* Footer */}
        <div className="pt-3 border-t border-amber-50 flex items-center justify-between">
          <span className="text-sm font-medium text-gray-600">
            {passengerCount} Traveler{passengerCount !== 1 ? "s" : ""}
          </span>
        </div>

        {/* Info Box */}
        <div className="flex items-start gap-2 bg-amber-50/50 p-3 rounded-xl border border-amber-100/50">
          <Warning weight="fill" className="size-4 text-amber-500 mt-0.5 shrink-0" />
          <p className="text-[11px] leading-relaxed text-amber-700 font-medium">
            Your flight is held. Ticket will be issued automatically once the installment loan is approved.
          </p>
        </div>
      </div>

      <div className="hidden md:flex flex-col border-l border-dashed border-amber-100 p-6 bg-amber-50/20 justify-center items-center w-28 relative">
        <div className="absolute -top-3 -left-3 size-6 bg-white border border-amber-50 rounded-full" />
        <div className="absolute -bottom-3 -left-3 size-6 bg-white border border-amber-50 rounded-full" />
        <Clock className="size-6 text-amber-300 mb-2" weight="duotone" />
        <span className="text-[10px] font-bold text-amber-600 uppercase tracking-widest text-center">In Review</span>
      </div>
    </div>
  );
};

const EmptyState = ({ tab }: { tab: TabKey }) => (
  <div className="flex flex-col text-center justify-center p-10 items-center gap-5 bg-white shadow-[0_4px_20px_-4px_rgba(15,23,42,0.10)] border border-gray-100 rounded-2xl">
    <img
      src={require("../../assets/images/payment-empty.png")}
      alt="empty"
      className="w-72"
    />
    <div className="flex flex-col text-center justify-center items-center">
      <div className="text-visaro-orange font-medium text-lg capitalize">
        {tab === "confirmed"
          ? "No Booked Flights"
          : tab === "pending"
          ? "No Pending Requests"
          : "No International Flights"}
      </div>
      <div className="text-gray-500 max-w-sm">
        {tab === "confirmed"
          ? "Oops! Looks like you don't have any booked flights yet."
          : tab === "pending"
          ? "Your pending installment bookings will appear here while awaiting approval."
          : "Flights booked through our international search will appear here."}
      </div>
    </div>
    {tab !== "pending" && (
      <Link
        to={tab === "international" ? "/aviation/international/v2/search" : "/aviation"}
        className="p-2 py-3 text-center bg-deepBlue text-white px-10 ring-2 ring-deepBlue/10 rounded-xl font-medium"
      >
        Book a Flight
      </Link>
    )}
  </div>
);

const tabs: { key: TabKey; label: string }[] = [
  { key: "confirmed", label: "Confirmed" },
  { key: "pending", label: "Pending" },
  { key: "international", label: "International" },
];

const BookedFlights = () => {
  const { state } = useLocation();
  const initialTab: TabKey = state?.tab === "international" ? "international" : "confirmed";
  const [activeTab, setActiveTab] = useState<TabKey>(initialTab);

  const {
    fetchingFlightBookings,
    bookedFlights,
    pendingFlights,
    refreshFlightBookings,
    isRefetchingFlightBookings,
  } = useFlightBookings();

  const {
    internationalBookings,
    fetchingInternationalBookings,
    refreshInternationalBookings,
    isRefetchingInternationalBookings,
  } = useInternationalFlightBookings();

  const tabCounts: Record<TabKey, number> = {
    confirmed: bookedFlights.length,
    pending: pendingFlights.length,
    international: internationalBookings.length,
  };

  const isRefetching = isRefetchingFlightBookings || isRefetchingInternationalBookings;

  const onRefresh = () => {
    refreshFlightBookings();
    refreshInternationalBookings();
  };

  return (
    <div className="min-h-screen bg-gray-50 relative overflow-hidden">
      <AviationAmbientBackground />

      <div className="relative w-full max-w-6xl mx-auto flex gap-6 px-5 md:px-10 py-10 flex-col">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div className="flex flex-col gap-1.5">
          <span className="text-[11px] font-bold uppercase tracking-widest text-gray-400">Aviation</span>
          <h1 className="text-2xl font-bold text-deepBlue">Your Flights</h1>
          <div className="text-gray-500 text-sm">
            Track your confirmed and pending flight bookings in one place.
          </div>
        </div>

        <button
          type="button"
          onClick={onRefresh}
          disabled={isRefetching}
          className="p-2 py-1.5 text-center text-deepBlue bg-white border px-4 rounded-xl font-medium flex gap-2 items-center border-gray-200 self-start text-sm shadow-sm transition-all hover:bg-gray-50 active:scale-95 disabled:opacity-50"
        >
          <ArrowClockwise
            className={`size-4 text-visaro-orange ${isRefetching ? "animate-spin" : ""}`}
            weight="bold"
          />
          <span>{isRefetching ? "Updating..." : "Refresh"}</span>
        </button>
      </div>

      {/* Tab Switcher */}
      <div className="bg-gray-100 p-1.5 rounded-xl max-w-[36rem] flex items-center gap-2">
        <AnimatePresence>
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              type="button"
              className="rounded-lg flex items-center text-center px-5 py-2.5 gap-2 cursor-pointer flex-1 justify-center relative"
            >
              {activeTab === tab.key && (
                <motion.div
                  layoutId="pill"
                  className="bg-white rounded-lg absolute top-0 left-0 w-full h-full shadow-sm"
                />
              )}
              <div className="flex items-center justify-center gap-2 relative z-1 text-nowrap">
                <span className={`text-sm font-bold ${activeTab === tab.key ? 'text-visaro-orange' : 'text-gray-500'}`}>
                  {tab.label === "Confirmed" ? "Booked" : tab.label} Flights
                </span>
                {!fetchingFlightBookings && tabCounts[tab.key] > 0 && (
                  <div className={`p-2 rounded-full text-[10px] font-bold leading-3 h-5 aspect-square flex items-center justify-center transition-colors ${
                    activeTab === tab.key
                      ? 'bg-visaro-orange text-white'
                      : 'bg-gray-200 text-gray-500'
                  }`}>
                    {tabCounts[tab.key]}
                  </div>
                )}
              </div>
            </button>
          ))}
        </AnimatePresence>
      </div>

        {/* Content */}
        {fetchingFlightBookings || fetchingInternationalBookings ? (
          <div className="flex flex-col items-center justify-center py-32 gap-4">
            <div className="relative">
              <HashLoader size={48} color="#1A1A31" />
              <div className="absolute inset-0 animate-ping rounded-full bg-deepBlue/5" />
            </div>
            <p className="text-deepBlue font-bold text-xs uppercase tracking-widest animate-pulse">
              Retrieving Flights...
            </p>
          </div>
        ) : (
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.4, ease: "easeOut" }}
          >
            {activeTab === "confirmed" ? (
              bookedFlights.length === 0 ? (
                <EmptyState tab="confirmed" />
              ) : (
                <BookedFlightsList bookedFlights={bookedFlights} hideTitle={true} />
              )
            ) : activeTab === "pending" ? (
              pendingFlights.length === 0 ? (
                <EmptyState tab="pending" />
              ) : (
                <div className="grid grid-cols-1 gap-6">
                  {pendingFlights.map((flight) => (
                    <PendingFlightCard key={flight.id} flight={flight} />
                  ))}
                </div>
              )
            ) : internationalBookings.length === 0 ? (
              <EmptyState tab="international" />
            ) : (
              <div className="grid grid-cols-1 gap-6">
                {internationalBookings.map((booking) => (
                  <IntlBookingCard key={booking.id} booking={booking} />
                ))}
              </div>
            )}
          </motion.div>
      )}
      </div>
    </div>
  );
};

export default BookedFlights;
